import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import ContactForm from '../components/ContactForm'
import GalleryLightbox from '../components/GalleryLightbox'
import { getResizedImageUrl } from '../lib/utils'

const SLIDESHOW_IMAGES = [
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7741-1024x681.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7675-1024x681.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/dji-0031-1024x512.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7641-1024x681.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7610.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7669.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/dji-0060-1024x501.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/dji-0067-1024x489.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/dsc-1090-1-1024x576.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7734-1024x681.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7688-1024x681.jpg'
]

const HERO_IMAGES = [
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7688-1024x681.jpg',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7669-jpg.webp',
  'https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-5926-1024x681.jpg'
]

export default function Home() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [visibleCount, setVisibleCount] = useState(4)
  const galleryImages = SLIDESHOW_IMAGES

  // Detect window width to set visibleCount for the carousel
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleCount(1)
      } else if (window.innerWidth < 768) {
        setVisibleCount(2)
      } else if (window.innerWidth < 1024) {
        setVisibleCount(3)
      } else if (window.innerWidth < 1280) {
        setVisibleCount(4)
      } else {
        setVisibleCount(5)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Clamp currentIndex on resize
  useEffect(() => {
    const maxIndex = Math.max(0, galleryImages.length - visibleCount)
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex)
    }
  }, [visibleCount, galleryImages.length, currentIndex])

  // Hero slideshow autoplay
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    // Set Forside metadata
    document.title = 'Sloth’s Maskinstation'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Sloth’s Maskinstation tilbyder landbrugsmaskinstation, entreprenør- og anlægsarbejde, maskinudlejning samt salg af sand, sten og grus i Bjerringbro og omegn.'
      )
    }
  }, [])

  // Autoplay carousel slideshow
  useEffect(() => {
    const maxIndex = Math.max(0, galleryImages.length - visibleCount)
    if (maxIndex === 0) return
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % (maxIndex + 1))
    }, 5000)
    return () => clearInterval(interval)
  }, [galleryImages.length, visibleCount])

  const businessAreas = [
    {
      title: 'Landbrugsmaskinstation',
      description: 'Totalløsning eller delvis pasning af dine marker. Vi tilbyder pløjning, såning, høstning, presning af baller samt klipning af læhegn.',
      icon: 'fa-tractor',
      href: '/landbrugsmaskinstation'
    },
    {
      title: 'Entreprenør & Anlæg',
      description: 'Alt i jord- og gravearbejde, belægning af indkørsler og terrasser, nedbrydning samt hurtig og effektiv snerydning.',
      icon: 'fa-excavator',
      href: '/entreprenoer-og-anlaeg'
    },
    {
      title: 'Maskinudlejning / Salg',
      description: 'Udlejning af professionelle maskiner og traktorer til landbrug og entreprenørarbejde. Fleksible løsninger til dine behov.',
      icon: 'fa-truck-ramp-box',
      href: '/maskinudlejning-salg'
    },
    {
      title: 'Salg af sand, sten & grus',
      description: 'Salg og levering af sand, flis, grus, stenmel, støbemix og stabilgrus. Mulighed for hent selv på vores plads i Bjerringbro.',
      icon: 'fa-mountain',
      href: '/sand-sten-grus'
    }
  ]

  const highlights = [
    {
      title: 'Etableret i 2016',
      description: 'Ejes og drives af Kristian Sloth med fokus på stærke kompetencer og personlig service.',
      icon: 'fa-calendar-days'
    },
    {
      title: 'Kvalitet i højsædet',
      description: 'For os er kvalitet en hobby. Vi leverer altid resultater i højeste kvalitet til aftalt tid.',
      icon: 'fa-award'
    },
    {
      title: 'Lokal forankring',
      description: 'Beliggende i Bjerringbro og dækker store dele af Jylland, herunder Viborg, Randers og Silkeborg.',
      icon: 'fa-house-chimney-user'
    },
    {
      title: 'Driftssikkerhed',
      description: 'Moderne maskinpark og driftssikkert udstyr sikrer, at vi kan løse opgaven effektivt.',
      icon: 'fa-shield-check'
    }
  ]

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative flex min-h-[85vh] lg:min-h-[95vh] items-center justify-center overflow-hidden bg-brand-dark text-white">
        {/* Hero Background Slideshow */}
        <div className="absolute inset-0">
          {HERO_IMAGES.map((imgUrl, idx) => (
            <img
              key={imgUrl}
              src={getResizedImageUrl(imgUrl, 'width=1920,quality=80,format=auto')}
              alt={`Sloth's Maskinstation hero background ${idx + 1}`}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
                idx === currentHeroIndex ? 'opacity-50 z-10' : 'opacity-0 z-0'
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/40 via-brand-dark/15 to-transparent z-10" />
          <div className="absolute inset-0 bg-black/10 z-10" />
        </div>

        <div className="relative z-20 mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-red px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm" style={{ textShadow: 'none' }}>
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
              Kvalitet er en hobby
            </span>
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl font-display leading-tight text-white">
              Sloth’s Maskinstation
            </h1>
            <p className="text-lg text-gray-100 leading-relaxed max-w-2xl">
              Professionel maskinstation og entreprenør i Bjerringbro nær Viborg, Randers og Silkeborg. Vi udfører alt i landbrugsarbejde, jordarbejde, have, indkørsler, smed og tømrerarbejde.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <a
                href="tel:004540420286"
                className="flex items-center gap-2 rounded-xl bg-brand-red px-6 py-4 text-sm font-bold text-white shadow-lg hover:bg-brand-red-hover transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
              >
                <i className="fa-regular fa-phone text-base" />
                Ring på +45 4042 0286
              </a>
              <a
                href="mailto:slothsmaskinstation@gmail.com"
                className="flex items-center gap-2 rounded-xl bg-white/10 border border-white/20 px-6 py-4 text-sm font-bold text-white hover:bg-white/20 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
              >
                <i className="fa-regular fa-envelope text-base text-brand-red" />
                Skriv til slothsmaskinstation@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl font-display border-l-4 border-brand-red pl-4">
                En lille virksomhed der besidder stor kvalitet inden for sit fag
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Sloth’s Maskinstation i Bjerringbro nær Viborg, Randers og Silkeborg startede tilbage i 2016 og ejes og drives i dag af Kristian Sloth. Sloth’s Maskinstation er en lille virksomhed, der altid har kvalitet i højsædet – kvalitet er en hobby.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Her hos Sloth’s Maskinstation hjælper vi både store og små kunder, og vi kan klare langt de fleste opgaver inden for vores fag. Vi tilbyder alt inden for landbrugsmaskinstation, entreprenørarbejde, havearbejde, indkørsler, smedearbejde, tømrerarbejde, maskinudlejning samt salg af sand, sten og grus.
              </p>
              <div className="pt-4">
                <Link
                  to="/om-os-galleri"
                  className="inline-flex items-center gap-2 text-sm font-bold text-brand-red hover:text-brand-red-hover transition-colors"
                >
                  Læs mere om os her
                  <i className="fa-regular fa-arrow-right" />
                </Link>
              </div>
            </div>

            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-xl border border-gray-100">
              <img
                src={getResizedImageUrl('https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/dji-0060.jpg', 'width=800,quality=85,format=auto')}
                alt="Sloth's Maskinstation maskiner på marken"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Primary Business Areas */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-sm font-bold uppercase tracking-wider text-brand-red">Hvad vi tilbyder</span>
            <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl font-display">
              Vores primære forretningsområder
            </h2>
            <p className="text-gray-600 text-sm md:text-base">
              Vi leverer professionelle løsninger til både landbrug, erhverv og private i hele Midtjylland.
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {businessAreas.map((area, index) => (
              <Link
                key={index}
                to={area.href}
                className="group rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between hover:-translate-y-0.5 cursor-pointer"
              >
                <div className="space-y-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-red/10 text-brand-red group-hover:bg-brand-red group-hover:text-white transition-colors">
                    <i className={`fa-regular ${area.icon} text-xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 font-display group-hover:text-brand-red transition-colors">{area.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{area.description}</p>
                </div>
                <div className="pt-6">
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-red group-hover:text-brand-red-hover transition-colors"
                  >
                    Læs mere
                    <i className="fa-regular fa-chevron-right text-[10px]" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights / Grounding Section */}
      <section className="py-20 bg-brand-dark text-white relative overflow-hidden">
        <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-brand-red/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid gap-12 lg:grid-cols-3 lg:items-center">
            <div className="lg:col-span-1 space-y-6">
              <span className="text-sm font-bold uppercase tracking-wider text-brand-red">Hvorfor vælge os?</span>
              <h2 className="text-3xl font-extrabold text-white md:text-4xl font-display leading-tight">
                Erfaring, lokal forankring & driftssikkerhed
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                Vi er en ung og dynamisk virksomhed fyldt med gå-på-mod. Sammen med et godt netværk og gode kompetente medarbejdere, danner det et stærkt fundament for Sloth’s Maskinstation.
              </p>
              <div className="pt-2">
                <a
                  href="tel:004540420286"
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-red px-5 py-3 text-xs font-bold text-white hover:bg-brand-red-hover transition-colors cursor-pointer"
                >
                  <i className="fa-regular fa-phone" />
                  Kontakt os i dag
                </a>
              </div>
            </div>

            <div className="lg:col-span-2 grid gap-6 sm:grid-cols-2">
              {highlights.map((hl, index) => (
                <div
                  key={index}
                  className="rounded-2xl border border-white/5 bg-white/5 p-6 space-y-3"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-red/20 text-brand-red">
                    <i className={`fa-regular ${hl.icon} text-lg`} />
                  </div>
                  <h3 className="text-base font-bold text-white font-display">{hl.title}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{hl.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      {galleryImages.length > 0 && (
        <section className="py-20 bg-white overflow-hidden">
          {/* Top area: horizontally centered text block constrained to a maximum width */}
          <div className="max-w-3xl mx-auto text-center mb-12 px-4 space-y-4">
            <span className="text-sm font-bold uppercase tracking-wider text-brand-red">Billeder fra marken</span>
            <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl font-display">
              Galleri preview
            </h2>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              Se et udvalg af vores maskiner og arbejde i marken. Vi leverer altid professionelle resultater med moderne udstyr.
            </p>
            <div className="pt-2">
              <Link
                to="/om-os-galleri"
                className="inline-flex items-center gap-2 text-sm font-bold text-brand-red hover:text-brand-red-hover transition-colors"
              >
                Se hele galleriet
                <i className="fa-regular fa-arrow-right" />
              </Link>
            </div>
          </div>

          {/* Bottom area: full-bleed horizontal image slider/carousel */}
          <div className="relative w-full">
            <div className="overflow-hidden w-full">
              <div
                className="flex gap-2 transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(calc(-${currentIndex} * (100% + 8px) / ${visibleCount}))`,
                }}
              >
                {galleryImages.map((img, idx) => (
                  <div
                    key={idx}
                    className="shrink-0 aspect-[3/4] relative group cursor-pointer overflow-hidden"
                    style={{
                      width: `calc((100% - ${(visibleCount - 1) * 8}px) / ${visibleCount})`,
                    }}
                    onClick={() => setLightboxIndex(idx)}
                  >
                    <img
                      src={getResizedImageUrl(img, 'width=600,height=800,fit=cover,quality=85,format=auto')}
                      alt={`Galleri billede ${idx + 1}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* Dark overlay on hover */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="bg-black/60 backdrop-blur-sm text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform scale-90 group-hover:scale-100">
                        <i className="fa-regular fa-magnifying-glass-plus text-sm" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Left and right navigation controls overlay the outermost images, centered vertically */}
            <button
              onClick={() => setCurrentIndex((prev) => (prev - 1 + (galleryImages.length - visibleCount + 1)) % (galleryImages.length - visibleCount + 1))}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-red"
              aria-label="Forrige billede"
            >
              <i className="fa-regular fa-chevron-left text-xl" />
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev + 1) % (galleryImages.length - visibleCount + 1))}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-red"
              aria-label="Næste billede"
            >
              <i className="fa-regular fa-chevron-right text-xl" />
            </button>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="relative py-24 overflow-hidden bg-brand-dark text-white">
        {/* Full-bleed background image with dark overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={getResizedImageUrl('https://assets.auxodev.dk/assets/518c2382-ce7b-462f-b5ef-9cfd635d4d85/bf0-7610-1024x681.jpg', 'width=1600,quality=80,format=auto')}
            alt="Baggrund"
            className="h-full w-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/80 via-brand-dark/65 to-brand-dark/50" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
            {/* Contact Info (Left Column) */}
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-3xl font-extrabold text-white md:text-4xl font-display">
                  Lad os tage en snak om din næste opgave
                </h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed max-w-xl">
                  Uanset om det drejer sig om maskinstationsarbejde, entreprenørarbejde eller noget helt tredje, så står vi klar til at hjælpe dig. Kontakt os for et uforpligtende prisoverslag.
                </p>
              </div>

              {/* Smaller attribution/link contact details */}
              <div className="space-y-4 pt-6 border-t border-white/10 max-w-xl">
                <div className="flex items-center gap-4 text-sm md:text-base text-gray-300">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-red/15 text-brand-red">
                    <i className="fa-regular fa-phone" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Ring direkte</p>
                    <a href="tel:004540420286" className="text-white hover:text-brand-red transition-colors font-bold">
                      +45 4042 0286
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm md:text-base text-gray-300">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-red/15 text-brand-red">
                    <i className="fa-regular fa-envelope" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Send en mail</p>
                    <a href="mailto:slothsmaskinstation@gmail.com" className="text-white hover:text-brand-red transition-colors font-bold break-all">
                      slothsmaskinstation@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm md:text-base text-gray-300">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-red/15 text-brand-red">
                    <i className="fa-regular fa-location-dot" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Adresse</p>
                    <p className="text-white font-bold">
                      Gullev Byvej 24, Gullev, 8850 Bjerringbro
                    </p>
                  </div>
                </div>
              </div>

              {/* Outlined primary action button (ghost button) */}
              <div className="pt-4">
                <a
                  href="tel:004540420286"
                  className="inline-flex items-center justify-center rounded-full border border-white/30 px-8 py-3.5 text-sm font-bold text-white hover:bg-white/10 hover:border-white transition-all duration-200 cursor-pointer gap-2"
                >
                  <i className="fa-regular fa-phone" />
                  Ring til os nu
                </a>
              </div>
            </div>

            {/* Contact Form (Right Column) */}
            <div className="bg-white/5 backdrop-blur-md p-8 md:p-10 rounded-3xl border border-white/10 shadow-2xl">
              <h3 className="text-2xl font-normal text-white mb-6 font-serif">Skriv til os</h3>
              <ContactForm variant="dark" />
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && galleryImages.length > 0 && (
        <GalleryLightbox
          images={galleryImages}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  )
}
